import { useBatchedSelectors } from '../../../store/kysely_module';
import { useModule } from '../../../hooks/useModule';
import { RecentActivityData, StarredData, ProjectWithStats, CollaborationRequestsData, UnreadNotificationsData, ProjectData } from '../types/dashboard.types';

export const useDashboardData = (workspaceId?: string) => {
  const userModule = useModule('User');
  const currentUser = userModule.currentUser.useState();

  const {results, isLoading} = useBatchedSelectors({
    recentActivity: {
      queryName: 'getDashboardData',
      args: { workspace_id: workspaceId },
      defaultValue: {
        projects: [],
        files: [],
        comments: [],
        attachments: [],
        myUserActivity: [],
        users: [],
        projectRevisions: []
      },
      workspaceContext: { workspace_id: null }
    },
    starredData: {
      queryName: 'getAllMyStars',
      args: {},
      defaultValue: {
        files: [],
        projects: []
      },
      workspaceContext: { workspace_id: null }
    },
    collaborationRequests: {
      queryName: 'getCollaborationRequests',
      args: {},
      defaultValue: [],
      workspaceContext: { workspace_id: null }
    },
    unreadNotifications: {
      queryName: 'getUnreadNotifications',
      args: {},
      defaultValue: [],
      workspaceContext: { workspace_id: null }
    }
  });

  const recentActivity = results.recentActivity.data;
  const starredData = results.starredData.data;
  const collaborationRequests = results.collaborationRequests.data;
  const unreadNotifications = results.unreadNotifications.data;

  // Process projects with additional stats
  const processProjects = (projectsToProcess: ProjectData[]): ProjectWithStats[] => {
    const attachmentsByProject = new Map<string, number>();
    const commentsByProject = new Map<string, number>();
    const peopleByProject = new Map<string, Set<string>>();
    const fileCommentsByProject = new Map<string, number>();

    // Count attachments and track people
    recentActivity?.attachments?.forEach((attachment) => {
      if (attachment.parent_type === 'Project') {
        const count = attachmentsByProject.get(attachment.parent_id) || 0;
        attachmentsByProject.set(attachment.parent_id, count + 1);

        if (!peopleByProject.has(attachment.parent_id)) {
          peopleByProject.set(attachment.parent_id, new Set());
        }
        peopleByProject.get(attachment.parent_id)?.add(attachment.created_by);
      }
    });

    // Count comments on projects and files within projects
    recentActivity?.attachments?.forEach((attachment) => {
      if (attachment.child_type === 'Comment') {
        if (attachment.parent_type === 'Project') {
          const count = commentsByProject.get(attachment.parent_id) || 0;
          commentsByProject.set(attachment.parent_id, count + 1);
        } else if (attachment.parent_type === 'File') {
          // Find which project this file belongs to
          const fileAttachment = recentActivity?.attachments?.find((a) =>
            a.child_type === 'File' && a.child_id === attachment.parent_id && a.parent_type === 'Project'
          );
          if (fileAttachment) {
            const count = fileCommentsByProject.get(fileAttachment.parent_id) || 0;
            fileCommentsByProject.set(fileAttachment.parent_id, count + 1);
          }
        }
      }
    });

    return projectsToProcess.map((project: ProjectData) => {
      let duration: number | undefined;
      if (project.attributes?.bounce_file) {
        const projectFileIds = recentActivity?.attachments?.filter(a =>
          a.parent_type === 'Project' &&
          a.parent_id === project.id &&
          a.child_type === 'File'
        ).map(a => a.child_id) || [];

        const projectFiles = recentActivity?.files?.filter(f => projectFileIds.includes(f.id)) || [];

        const bounceFile = projectFiles.find(f =>
          f.relative_path === project.attributes.bounce_file
        );

        if (bounceFile?.attributes?.duration) {
          duration = parseFloat(bounceFile.attributes.duration);
        }
      }

      const projectComments = (commentsByProject.get(project.id) || 0) + (fileCommentsByProject.get(project.id) || 0);

      return {
        ...project,
        numberOfPeople: peopleByProject.get(project.id)?.size || 1,
        numberOfComments: projectComments,
        isStarred: starredData.projects.some((p) => p.id === project.id),
        duration,
      };
    });
  };

  const myProjects = processProjects(
    recentActivity?.projects?.filter((p) =>
      currentUser && p.created_by === currentUser.id
    ) || []
  );

  const sharedProjects = processProjects(
    recentActivity?.projects?.filter((p) =>
      currentUser && p.created_by !== currentUser.id
    ) || []
  );

  const starredProjects = processProjects(starredData?.projects || []);

  const refresh = () => {
    results.recentActivity.refetch();
    results.starredData.refetch();
    results.collaborationRequests.refetch();
    results.unreadNotifications.refetch();
  };

  return {
    recentActivity,
    starredData,
    myProjects,
    sharedProjects,
    starredProjects,
    starredFiles: starredData.files,
    collaborationRequests,
    unreadNotifications,
    isLoading,
    refresh
  };
};
